import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cls from 'clsx';
import { Left as PrevYear, Right as NextYear } from '@linktivity/link-icons';

import { MONTH_COUNTS } from './constants';
import { useDateContext } from './context';
import { IMonthTableProps } from './DatePicker.types';
import styles from './datepicker.module.css';

const MonthTable: React.FC<IMonthTableProps> = () => {
  const { t } = useTranslation();
  const { currentYear, currentMonth, setCurrentDate, setView, backToDateView } =
    useDateContext();
  const prevYear = useCallback(() => {
    setCurrentDate(
      prevDate => new Date(currentYear - 1, currentMonth, prevDate.getDate())
    );
  }, [currentYear, currentMonth, setCurrentDate]);

  const nextYear = useCallback(() => {
    setCurrentDate(
      prevDate => new Date(currentYear + 1, currentMonth, prevDate.getDate())
    );
  }, [currentYear, currentMonth, setCurrentDate]);

  const months = useMemo(() => {
    return Array.from(Array(MONTH_COUNTS).keys());
  }, []);

  const selectMonth = useCallback(
    (month: number) => {
      setCurrentDate(
        prevDate => new Date(prevDate.getFullYear(), month, prevDate.getDate())
      );
      backToDateView();
    },
    [setCurrentDate, backToDateView]
  );

  return (
    <div className={styles.monthTable}>
      <div className={styles.monthTableHeader}>
        <button
          type="button"
          className={styles.monthTableButton}
          onClick={prevYear}
        >
          <PrevYear className={styles.monthTableIcon} />
        </button>

        <button
          type="button"
          className={styles.monthTableCurrent}
          onClick={() => setView('year')}
        >
          {currentYear}
        </button>
        <button
          type="button"
          className={styles.monthTableButton}
          onClick={nextYear}
        >
          <NextYear className={styles.monthTableIcon} />
        </button>
      </div>
      <div className={styles.monthTableBody}>
        {months.map(month => (
          <div className={styles.monthTableCell} key={month}>
            <button
              type="button"
              className={cls(styles.monthTableCellButton, {
                [styles.current]: month === currentMonth
              })}
              onClick={() => selectMonth(month)}
            >
              {t(`date.month.month${month + 1}`)}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MonthTable;
